import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getSavedExtracts,
  getSavedKeywordScans,
  KeywordByAsinRow,
  SavedKeywordScan,
  BrandInfo,
  TopListing,
  estimateSalesToFirstPage,
} from "../../services/jungleScout";
import CardComponent from "../shared/CardComponent";
import TableComponent, { Column } from "../shared/TableComponent";

type SavedExtract = {
  id: string;
  asin: string;
  marketplace: string;
  extractedAt: string;
  keywords: KeywordByAsinRow[];
};

export default function KeywordsSaved() {
  const { t } = useTranslation();
  const module = "modules.amazonExtract.";
  const keywordsModule = "modules.keywordsSaved.";
  const [loading, setLoading] = useState(true);
  const [extracts, setExtracts] = useState<SavedExtract[]>([]);
  const [scans, setScans] = useState<SavedKeywordScan[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedScanId, setExpandedScanId] = useState<string | null>(null);

  const loadExtracts = useCallback(async () => {
    setLoading(true);
    try {
      const [extractList, scanList] = await Promise.all([
        getSavedExtracts(),
        getSavedKeywordScans(),
      ]);
      setExtracts(extractList);
      setScans(scanList);
    } catch {
      setExtracts([]);
      setScans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExtracts();
  }, [loadExtracts]);

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const formatNumber = (v: number | null | undefined) =>
    v != null ? String(v) : "—";
  const formatCurrency = (v: number | null | undefined) =>
    v != null ? `$${Number(v).toFixed(2)}` : "—";

  const getKeywordColumns = (): Column[] => [
    {
      header: t(module + "keyword"),
      accessor: "attributes.name",
      cell: (_, row) => (
        <Typography variant="body2">
          {row?.attributes?.name ?? "—"}
        </Typography>
      ),
    },
    {
      header: t(module + "organicRank"),
      accessor: "attributes.organic_rank",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.organic_rank)}
        </Typography>
      ),
    },
    {
      header: t(module + "sponsoredRank"),
      accessor: "attributes.sponsored_rank",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.sponsored_rank)}
        </Typography>
      ),
    },
    {
      header: t(module + "overallRank"),
      accessor: "attributes.overall_rank",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.overall_rank)}
        </Typography>
      ),
    },
    {
      header: t(module + "salesToFirstPage"),
      accessor: "salesToFirstPage",
      cell: (data) => (
        <Typography variant="body2">{formatNumber(data)}</Typography>
      ),
    },
    {
      header: t(module + "ppcBidFirstPage"),
      accessor: "attributes.ppc_bid_exact",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatCurrency(row?.attributes?.ppc_bid_exact)}
        </Typography>
      ),
    },
    {
      header: t(module + "ppcBidBroad"),
      accessor: "attributes.ppc_bid_broad",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatCurrency(row?.attributes?.ppc_bid_broad)}
        </Typography>
      ),
    },
    {
      header: t(module + "searchVolumeExact"),
      accessor: "attributes.monthly_search_volume_exact",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.monthly_search_volume_exact)}
        </Typography>
      ),
    },
  ];

  const keywordColumns = getKeywordColumns();

  const brandColumns: Column[] = [
    { header: t(module + "brandName"), accessor: "brandName", cell: (_, row: BrandInfo) => <Typography variant="body2">{row.brandName}</Typography> },
    { header: t(module + "totalListings"), accessor: "totalListings", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatNumber(row.totalListings)}</Typography> },
    { header: t(module + "averagePosition"), accessor: "averagePosition", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatNumber(Math.round(row.averagePosition))}</Typography> },
    { header: t(module + "averagePrice"), accessor: "averagePrice", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatCurrency(row.averagePrice)}</Typography> },
    { header: t(module + "organicListingsCount"), accessor: "organicListingsCount", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatNumber(row.organicListingsCount)}</Typography> },
    { header: t(module + "organicAveragePosition"), accessor: "organicAveragePosition", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatNumber(Math.round(row.organicAveragePosition))}</Typography> },
    { header: t(module + "organicAveragePrice"), accessor: "organicAveragePrice", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatCurrency(row.organicAveragePrice)}</Typography> },
    { header: t(module + "sponsoredListingsCount"), accessor: "sponsoredListingsCount", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatNumber(row.sponsoredListingsCount)}</Typography> },
    { header: t(module + "sponsoredAveragePosition"), accessor: "sponsoredAveragePosition", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatNumber(Math.round(row.sponsoredAveragePosition))}</Typography> },
    { header: t(module + "sponsoredAveragePrice"), accessor: "sponsoredAveragePrice", cell: (_, row: BrandInfo) => <Typography variant="body2">{formatCurrency(row.sponsoredAveragePrice)}</Typography> },
  ];

  const topListingsColumns: Column[] = [
    { header: t(module + "asin"), accessor: "asin", cell: (_, row: TopListing) => <Typography variant="body2">{row.asin}</Typography> },
    { header: t(module + "title"), accessor: "title", cell: (_, row: TopListing) => <Typography variant="body2">{row.title}</Typography> },
    { header: t(module + "brand"), accessor: "brand", cell: (_, row: TopListing) => <Typography variant="body2">{row.brand}</Typography> },
    { header: t(module + "visits"), accessor: "visits", cell: (_, row: TopListing) => <Typography variant="body2">{formatNumber(row.visits)}</Typography> },
    { header: t(module + "purchases"), accessor: "purchases", cell: (_, row: TopListing) => <Typography variant="body2">{formatNumber(row.purchases)}</Typography> },
    { header: t(module + "conversionRate"), accessor: "conversionRate", cell: (_, row: TopListing) => <Typography variant="body2">{row.conversionRate != null ? `${Number(row.conversionRate).toFixed(2)}%` : "—"}</Typography> },
  ];

  const tableDataForExtract = (keywords: KeywordByAsinRow[]) =>
    keywords.map((k) => ({
      ...k,
      "attributes.name": k.attributes?.name,
      "attributes.organic_rank": k.attributes?.organic_rank,
      "attributes.sponsored_rank": k.attributes?.sponsored_rank,
      "attributes.overall_rank": k.attributes?.overall_rank,
      salesToFirstPage: estimateSalesToFirstPage(k.attributes),
      "attributes.ppc_bid_exact": k.attributes?.ppc_bid_exact,
      "attributes.ppc_bid_broad": k.attributes?.ppc_bid_broad,
      "attributes.monthly_search_volume_exact":
        k.attributes?.monthly_search_volume_exact,
    }));

  return (
    <CardComponent title={t(keywordsModule + "title")} isLoading={loading}>
      <Box>
        {/* Saved keyword scans (from Amazon Scanner) */}
        <Typography variant="h6" style={{ marginTop: 0, marginBottom: 8 }}>
          {t(keywordsModule + "savedScans")}
        </Typography>
        {scans.length === 0 && extracts.length === 0 && !loading && (
          <Typography color="textSecondary" style={{ marginBottom: 24 }}>
            {t(keywordsModule + "noSavedScans")}
          </Typography>
        )}
        {scans.length > 0 && (
          <Table size="small" style={{ marginBottom: 24 }}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t(keywordsModule + "keyword")}</TableCell>
                <TableCell>{t(keywordsModule + "marketplace")}</TableCell>
                <TableCell>{t(keywordsModule + "extractedAt")}</TableCell>
                <TableCell align="right">{t(module + "searchVolume30Days")}</TableCell>
                <TableCell align="right">{t(module + "averageAdValue")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scans.map((scan) => {
                const isExpanded = expandedScanId === scan.id;
                const brands = scan.brands ?? [];
                const topListings = scan.topListings ?? [];
                return (
                  <React.Fragment key={scan.id}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedScanId(isExpanded ? null : scan.id)
                          }
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{scan.keyword}</TableCell>
                      <TableCell>{scan.marketplace.toUpperCase()}</TableCell>
                      <TableCell>{formatDate(scan.extractedAt)}</TableCell>
                      <TableCell align="right">{formatNumber(scan.searchVolume30Days)}</TableCell>
                      <TableCell align="right">{formatCurrency(scan.averageAdValue)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box margin={2}>
                            {brands.length > 0 && (
                              <>
                                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                                  {t(module + "brandInformation")}
                                </Typography>
                                <TableComponent
                                  data={brands}
                                  columns={brandColumns}
                                  perPage={10}
                                  searchKeys={["brandName"]}
                                />
                                <Box style={{ height: 16 }} />
                              </>
                            )}
                            {topListings.length > 0 && (
                              <>
                                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                                  {t(module + "topListings")}
                                </Typography>
                                <TableComponent
                                  data={topListings}
                                  columns={topListingsColumns}
                                  perPage={10}
                                  searchKeys={["asin", "title", "brand"]}
                                />
                              </>
                            )}
                            {brands.length === 0 && topListings.length === 0 && (
                              <Typography color="textSecondary" variant="body2">
                                {t(module + "noData")}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}

        
      </Box>
    </CardComponent>
  );
}
